import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  X,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Music,
  MapPin,
  Calendar,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { Badge } from "../../components/ui/Badge";
import { EntityLink, EntityLinkList } from "../../components/ui/EntityLink";
import { Pagination } from "../../components/ui/Pagination";
import { CitySelect } from "../../components/ui/CitySelect";
import ImageListEditor from "../../components/ui/ImageListEditor";
import { eventsApi, imageReviewApi } from "../../utils/api";
import {
  capitalize,
  displayLabel,
  emptyImageReviewStats,
  getAdminEntityUrl,
  getEventWebUrl,
  normalizeImageRefs as normalizeEventImages,
  normalizeImageReviewStats,
  reconcileImageUrls,
  removeFromImageReviewStats,
} from "../../utils/helpers";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import ImageBlocklistView from "./ImageBlocklistView";
import ImageSuggestionsPanel from "./ImageSuggestionsPanel";
import UrlThumbnail from "./UrlThumbnail";
import EventDisplayImage, { ARTIST_IMAGE_FALLBACK } from "./EventDisplayImage";
import { EventStatusBadge } from "../../components/ui/EventStatusBadge";

// ─── helpers ────────────────────────────────────────────────────────────────

const IMAGE_RESOLUTION_META = {
  bad: { label: "Bad image shown", className: "border-red-300 bg-red-50 text-red-700" },
  artist_fallback: { label: "Artist fallback", className: "border-green-300 bg-green-50 text-green-700" },
  event_fallback: { label: "Event image fallback", className: "border-blue-300 bg-blue-50 text-blue-700" },
};

function formatOccDate(local_date) {
  if (!local_date) return null;
  try {
    return new Date(local_date + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
  } catch { return local_date; }
}

// ─── Main view ───────────────────────────────────────────────────────────────

export default function ImageReviewView({ onQueueChange }) {
  // Inner sub-tab: which view inside Image Alerts is active
  const [innerTab, setInnerTab] = useState("resolve"); // "resolve" | "flagged" | "blocklist"

  // Flagged URLs sub-tab state — fetched from server-side aggregation so counts
  // reflect every needs_review row, not just the per-event queue window.
  const FLAGGED_PAGE_SIZE = 25;
  const [flaggedPage, setFlaggedPage] = useState(1);
  const [flaggedEntries, setFlaggedEntries] = useState([]);
  const [flaggedTotal, setFlaggedTotal] = useState(0);
  const [flaggedLoading, setFlaggedLoading] = useState(false);
  const [flaggedError, setFlaggedError] = useState(null);
  // Separate from flaggedError: that one means "the list failed to load" and the renderer
  // returns an error panel instead of the table, so putting a per-row action failure in it
  // would erase the list, its pagination and any expanded row over one failed Block.
  const [blockError, setBlockError] = useState(null);
  // Which flagged row has its event list open, and which row's Block is in flight.
  const [expandedUrl, setExpandedUrl] = useState(null);
  const [pendingUrl, setPendingUrl] = useState(null);

  // Queue state
  const [queue, setQueue]       = useState([]);   // [{event_id, suspicious_image_urls, artist_count, occurrence_count}]
  const [stats, setStats]       = useState(emptyImageReviewStats);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError]     = useState(null);
  const [cursor, setCursor]     = useState(0);    // current index in the loaded queue
  const [cityFilter, setCityFilter] = useState(""); // "" = all cities
  // Submit-to-search (not filter-as-you-type): the queue is fetched server-side, like
  // every other admin list search.
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();

  // Full event detail for the current item
  const [eventDetail, setEventDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Editing state
  const [currentImages, setCurrentImages] = useState([]);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Load queue ──────────────────────────────────────────────────────────
  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    setQueueError(null);
    try {
      const data = await imageReviewApi.getQueue(100, cityFilter || null, search || null);
      const events = data.events || [];
      const nextStats = normalizeImageReviewStats(data);
      setQueue(events);
      setStats(nextStats);
      // stats.total is the filtered count when a city or search is set; only the
      // unfiltered load reflects the app-wide pending count, so only then update the badge.
      if (!cityFilter && !search) onQueueChange?.(nextStats.total);
      setCursor(0);
    } catch {
      setQueueError("Could not load image review queue.");
    } finally {
      setQueueLoading(false);
    }
  }, [onQueueChange, cityFilter, search]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // ── Load flagged URLs aggregation when the sub-tab is active or its page changes ──
  const loadFlaggedUrls = useCallback(async (pageNum) => {
    setFlaggedLoading(true);
    setFlaggedError(null);
    try {
      const data = await imageReviewApi.getFlaggedUrls(pageNum, FLAGGED_PAGE_SIZE);
      setFlaggedEntries(data.entries || []);
      setFlaggedTotal(data.total || 0);
      return data.entries || [];
    } catch (e) {
      setFlaggedError(e?.response?.data?.detail || "Could not load flagged URLs.");
      return null;
    } finally {
      setFlaggedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (innerTab === "flagged") loadFlaggedUrls(flaggedPage);
  }, [innerTab, flaggedPage, loadFlaggedUrls]);

  // Confirm one URL as a placeholder straight from the aggregation — the granular
  // Every row action reloads the same way and fails the same way, so they share one runner:
  // only the call and the message differ. `pendingUrl` disables every row's buttons while one
  // is in flight, which also stops a second click racing the reload.
  const runRowAction = async (url, action, describe) => {
    setPendingUrl(url);
    setBlockError(null);
    try {
      setToast(describe(await action(url)));
      // Refetch rather than mark the row locally. Every action here moves the row between
      // sources server-side — blocking deletes a detected row, clearing turns any row into an
      // allowed one — so a locally-marked row keeps rendering for a listing that has changed
      // under it, and a per-session set of acted-on URLs would never reset.
      setExpandedUrl(null);
      // Reload, then step back only if the page actually came back empty. Whether a row
      // disappears depends on its kind — a `detected` row is deleted by the block, while a
      // queued one keeps its open review row and stays listed — so predicting it client-side
      // would bounce the admin off a page whose row is still there. `null` means the reload
      // itself failed; leave the page alone in that case.
      const remaining = await loadFlaggedUrls(flaggedPage);
      if (remaining && !remaining.length && flaggedPage > 1) setFlaggedPage((p) => p - 1);
    } catch (e) {
      setBlockError(e?.response?.data?.detail || "Could not update this image.");
    } finally {
      setPendingUrl(null);
    }
  };

  // Confirm one URL as a placeholder straight from the aggregation — the granular alternative
  // to Resolve, which blocklists every suspicious URL on one event at once.
  const handleBlockUrl = (url) =>
    runRowAction(url, (u) => imageReviewApi.blockUrls([u]), ({ added }) =>
      added ? "Image blocked." : "Image was already on the blocklist.",
    );

  // Clearing is durable, not a dismissal: the scan subtracts allowed URLs, so it will not
  // re-detect this one on the same evidence next run.
  const handleAllowUrl = (url) =>
    runRowAction(url, imageReviewApi.allowUrl, ({ allowed, capped }) =>
      capped
        ? "Marked OK, but too many events carry it to restore in one pass."
        : allowed
          ? "Marked OK — the scan will stop flagging this image."
          : "Image was already marked OK.",
    );

  // Undo restores `detected` rather than deleting the row, so the URL stays on this screen
  // with both actions available instead of disappearing until the next scan.
  const handleUnallowUrl = (url) =>
    runRowAction(url, imageReviewApi.unallowUrl, () => "Back to flagged — the image is demoted again.");

  // ── Load full event detail whenever cursor changes ───────────────────────
  // The queue is city- and search-filtered server-side (loadQueue refetches on change).
  const currentItem = queue[cursor] ?? null;

  useEffect(() => {
    if (!currentItem) { setEventDetail(null); return; }
    // Capture the snapshot values used inside the async chain so the effect
    // only needs to depend on the event_id (re-running on ID change only,
    // not on every queue array rebuild that leaves the ID unchanged).
    const eventId = currentItem.event_id;
    const fallbackImages = normalizeEventImages(currentItem.images);
    const suspiciousUrls = new Set(currentItem.suspicious_image_urls || []);
    setPreviewImageUrl(
      currentItem.display_image?.url
        || currentItem.fallback_image?.url
        || fallbackImages[0]?.url
        || null
    );
    setEventDetail(null);
    setDetailLoading(true);
    setActionError(null);

    eventsApi.getById(eventId)
      .then((res) => {
        const raw = res?.event ?? res ?? {};
        setEventDetail(raw);
        // Known placeholders are removed from the pending edit by default. They
        // remain available in the collapsed evidence section for inspection.
        setCurrentImages(normalizeEventImages(raw.images).filter((image) => !suspiciousUrls.has(image.url)));
      })
      .catch(() => {
        setCurrentImages(fallbackImages.filter((image) => !suspiciousUrls.has(image.url)));
      })
      .finally(() => setDetailLoading(false));
  }, [currentItem?.event_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ──────────────────────────────────────────────────────────────
  const advanceQueue = (eventId, msg) => {
    const removedItem = queue.find((item) => item.event_id === eventId);
    const nextQueue = queue.filter((i) => i.event_id !== eventId);
    const nextStats = removeFromImageReviewStats(stats, removedItem?.image_resolution_status);
    setQueue(nextQueue);
    setStats(nextStats);
    // `stats.total` is a subset count whenever a city or search filter is set, so it can only be
    // published as the app-wide badge on the unfiltered queue. Under a filter, re-read the
    // dedicated count endpoint instead of leaving the badge one too high.
    if (!cityFilter && !search) {
      onQueueChange?.(nextStats.total);
    } else {
      imageReviewApi
        .getCount()
        .then((data) => onQueueChange?.(data.total || 0))
        .catch(() => {});
    }
    setCursor((c) => Math.min(c, Math.max(0, nextQueue.length - 1)));
    setToast(msg);
  };

  const handleSaveAndResolve = async () => {
    if (!currentItem) return;
    setActionLoading(true);
    setActionError(null);
    try {
      // Persist the chosen public image first so a later review-state failure cannot
      // leave Elasticsearch serving the old image after the ticket has disappeared.
      const selectedUrl = normalizeEventImages(currentImages)[0]?.url || null;
      await eventsApi.update(currentItem.event_id, {
        images: currentImages,
        primary_image_url: selectedUrl,
      });
      await imageReviewApi.resolve(currentItem.event_id);
      advanceQueue(currentItem.event_id, "✓ Images updated and review resolved.");
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Could not save images.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (!currentItem) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await imageReviewApi.dismiss(currentItem.event_id);
      advanceQueue(currentItem.event_id, "✓ Review dismissed — image marked as acceptable.");
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Could not dismiss review.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleImagesChange = (nextUrls) => {
    setCurrentImages(reconcileImageUrls(currentImages, nextUrls));
    setPreviewImageUrl(nextUrls[0] || null);
  };

  const handleSuggestedImagesChange = (nextImages) => {
    const normalized = normalizeEventImages(nextImages);
    setCurrentImages(normalized);
    setPreviewImageUrl(normalized[0]?.url || null);
  };

  // ── Derived display values ────────────────────────────────────────────────
  const cityLabel = cityFilter ? capitalize(cityFilter) : "";

  // Artists from full detail or fallback to queue data
  const artists = eventDetail?.artists || currentItem?.artists || [];
  const occurrences = eventDetail?.occurrences || [];
  const venue  = eventDetail?.venue || null;
  const domain = eventDetail?.domain || [];
  const status = eventDetail?.display_status || eventDetail?.status || currentItem?.event_status || "";
  const classification = eventDetail?.classification_profile || null;
  const venueName    = venue?.name   || eventDetail?.venue_name   || currentItem?.venue_name || "";
  const venueCity    = venue?.city   || eventDetail?.city         || currentItem?.city        || "";
  const venueId      = venue?.id     || eventDetail?.venue_id     || currentItem?.venue_id    || null;

  // The provider's own title, and the curated label the admin (and every consumer) reads.
  const title = eventDetail?.title || currentItem?.title || currentItem?.event_id || "";
  // Not the usual `detail || queue` chain: `preferred_title` is clearable, so falling back to the
  // queue row would keep printing a label the admin has just deleted.
  const preferredTitle = (eventDetail ? eventDetail.preferred_title : currentItem?.preferred_title) || "";
  const displayTitle = displayLabel(preferredTitle, title);
  const firstOcc = occurrences[0];
  const resolutionMeta = IMAGE_RESOLUTION_META[currentItem?.image_resolution_status];
  const { total, eventImageFallbackCount, artistFallbackCount, badImageCount } = stats;
  const applaussEventUrl = currentItem?.event_id ? getEventWebUrl(currentItem.event_id) : null;
  const adminEventUrl = getAdminEntityUrl("event", currentItem?.event_id);
  const flaggedImageUrls = new Set(currentItem?.suspicious_image_urls || []);
  const originalImages = normalizeEventImages([
    ...(eventDetail?.images || currentItem?.images || []),
    ...(currentItem?.suspicious_image_urls || []).map((url) => ({ url, source: "unknown" })),
  ]).filter((image, index, images) => images.findIndex((candidate) => candidate.url === image.url) === index);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Inner tab switcher */}
      <div className="flex w-full items-center gap-0.5 overflow-x-auto rounded-md border border-border bg-muted/40 p-0.5 sm:w-fit">
        <button
          onClick={() => setInnerTab("resolve")}
          className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
            innerTab === "resolve"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Resolve
        </button>
        <button
          onClick={() => setInnerTab("flagged")}
          className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
            innerTab === "flagged"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Flagged URLs
        </button>
        <button
          onClick={() => setInnerTab("blocklist")}
          className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
            innerTab === "blocklist"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Blocklist
        </button>
      </div>

      {innerTab === "blocklist" && <ImageBlocklistView />}

      {innerTab === "resolve" && <>
      {/* Top bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-base font-semibold">Image Review</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div
            className="flex flex-wrap items-center gap-2 text-xs"
            aria-label={`${total} total alerts${cityLabel ? ` in ${cityLabel}` : ""} equals ${badImageCount} bad images plus ${artistFallbackCount} artist fallbacks plus ${eventImageFallbackCount} event image fallbacks`}
          >
            <span className="font-semibold text-foreground">
              {total} total{cityLabel ? ` in ${cityLabel}` : ""}
            </span>
            <span className="font-medium text-destructive">{badImageCount} bad</span>
            <span className="font-medium text-green-700">{artistFallbackCount} artist</span>
            <span className="font-medium text-blue-700">{eventImageFallbackCount} event</span>
          </div>
          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={submitSearch}
            onClear={clearSearch}
            placeholder="Search title or event ID"
            loading={queueLoading}
          />
          <CitySelect value={cityFilter} onChange={setCityFilter} />
          <Button variant="outline" size="sm" className="gap-1.5" onClick={loadQueue} disabled={queueLoading}>
            <RefreshCw className={`h-3.5 w-3.5 ${queueLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-800">{toast}</div>
      )}

      {/* Queue error */}
      {queueError && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{queueError}</div>
      )}

      {/* Loading queue */}
      {queueLoading && (
        <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      )}

      {/* Empty state */}
      {!queueLoading && !queueError && queue.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
          {/* A search miss is not an all-clear, so it gets no green check. */}
          {search ? (
            <p className="text-sm font-medium">
              No open image alert matches “{search}”{cityFilter ? ` in ${cityLabel}` : ""}.
            </p>
          ) : cityFilter ? (
            <>
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="text-sm font-medium">No flagged images in {cityLabel}</p>
              <p className="text-xs">Clear the city filter to see all pending reviews.</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="text-sm font-medium">All images look good</p>
              <p className="text-xs">No events currently flagged for image review.</p>
            </>
          )}
        </div>
      )}

      {/* Main card */}
      {!queueLoading && queue.length > 0 && (
        <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">

          {/* Navigation header */}
          <div className="flex flex-col gap-3 border-b border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium text-muted-foreground">
                {cursor + 1} / {queue.length}
              </span>
              {resolutionMeta && (
                <Badge variant="outline" className={resolutionMeta.className}>{resolutionMeta.label}</Badge>
              )}
              {!detailLoading && originalImages.length === 0 && (
                <Badge variant="outline" className="border-destructive/40 text-destructive">No images</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCursor((c) => Math.max(0, c - 1))}
                disabled={cursor === 0}
                className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setCursor((c) => Math.min(queue.length - 1, c + 1))}
                disabled={cursor === queue.length - 1}
                className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {detailLoading ? (
            <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading event…</span>
            </div>
          ) : (
            <div className="flex flex-col gap-4 p-4 sm:p-5">

              {/* ── Event info (mirrors detail panel style) ── */}
              <div className="flex flex-col gap-3 sm:flex-row">
                {/* Exact image selected by the consumer event view. */}
                <EventDisplayImage
                  src={previewImageUrl || originalImages[0]?.url}
                  alt={title}
                  imageResolutionStatus={
                    currentItem?.image_resolution_status === ARTIST_IMAGE_FALLBACK
                    && previewImageUrl === currentItem?.display_image?.url
                      ? ARTIST_IMAGE_FALLBACK
                      : null
                  }
                  compactLabel
                  className="h-24 w-full rounded-lg ring-1 ring-border sm:w-24 sm:shrink-0"
                />

                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <h3 className="text-lg font-bold leading-snug">{displayTitle}</h3>
                    <ProviderLabel preferred={preferredTitle} original={title} />
                    <p className="text-[11px] font-mono text-muted-foreground mt-0.5 select-all">
                      {currentItem?.event_id}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <EventStatusBadge status={status} />
                      {domain.map((d) => (
                        <Badge key={d} variant="outline">{capitalize(d)}</Badge>
                      ))}
                      {classification?.primary_event_type && (
                        <Badge variant="secondary">{capitalize(classification.primary_event_type)}</Badge>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {artists.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Music className="h-3.5 w-3.5" />
                          <EntityLinkList type="artist" items={artists.filter((artist) => artist.name)} newTab />
                        </span>
                      )}
                      {venueName && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          <EntityLink type="venue" id={venueId} newTab>{venueName}</EntityLink>
                          {venueCity && ` · ${venueCity}`}
                        </span>
                      )}
                      {firstOcc && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatOccDate(firstOcc.local_date)}
                          {firstOcc.local_time && ` · ${firstOcc.local_time.slice(0, 5)}`}
                          {occurrences.length > 1 && ` · +${occurrences.length - 1}`}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {applaussEventUrl && (
                        <a
                          href={applaussEventUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full border border-blue-600 px-2.5 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Open in Applauss
                        </a>
                      )}
                      {adminEventUrl && (
                        <a
                          href={adminEventUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Open in Admin
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {originalImages.length > 0 && (
                <details className="group overflow-hidden rounded-lg border border-border bg-background">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-xs font-medium hover:bg-muted/30">
                    <span>
                      Original images ({originalImages.length})
                      {flaggedImageUrls.size > 0 && (
                        <span className="ml-2 font-normal text-destructive">
                          {flaggedImageUrls.size} flagged
                        </span>
                      )}
                    </span>
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <span className="group-open:hidden">Show</span>
                      <span className="hidden group-open:inline">Hide</span>
                      <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                    </span>
                  </summary>
                  <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-2 lg:grid-cols-3">
                    {originalImages.map((image) => {
                      const flagged = flaggedImageUrls.has(image.url);
                      return (
                        <a
                          key={image.url}
                          href={image.url}
                          target="_blank"
                          rel="noreferrer"
                          className={`overflow-hidden rounded-md border bg-background ${
                            flagged ? "border-destructive/40" : "border-border"
                          }`}
                        >
                          <a href={image.url} target="_blank" rel="noreferrer" title="Open image in a new tab">
                            <img
                              src={image.url}
                              alt={flagged ? "Flagged event image" : "Original event image"}
                              className={`aspect-video w-full cursor-zoom-in bg-muted object-cover ${flagged ? "opacity-70" : ""}`}
                            />
                          </a>
                          <div className="flex items-center justify-between gap-2 px-2 py-1.5 text-[10px]">
                            <span className="truncate text-muted-foreground" title={image.url}>{image.source}</span>
                            {flagged && <span className="shrink-0 font-medium text-destructive">Flagged</span>}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </details>
              )}

              {/* ── Resolution workspace ── */}
              <div className="order-1 space-y-3 rounded-lg border border-primary/20 bg-primary/[0.02] p-3">
                <ImageSuggestionsPanel
                  eventId={currentItem?.event_id}
                  currentImages={currentImages}
                  suspiciousUrls={currentItem?.suspicious_image_urls || []}
                  onChange={handleSuggestedImagesChange}
                />
                <ImageListEditor
                  value={normalizeEventImages(currentImages).map((image) => image.url)}
                  onChange={handleImagesChange}
                  title="Selected"
                  renderBadge={(url) => {
                    const source = normalizeEventImages(currentImages).find((image) => image.url === url)?.source;
                    return source ? <Badge variant="secondary" className="text-[10px]">{source}</Badge> : null;
                  }}
                />

                {/* Google Images search link — quick way to find a replacement */}
                {(title || venueName) && (
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(`${title} ${venueName}`.trim())}&tbm=isch&udm=2`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    Google Images
                  </a>
                )}
              </div>

              {/* Action error */}
              {actionError && (
                <p className="order-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{actionError}</p>
              )}

              {/* ── Action bar ── */}
              <div className="order-2 flex flex-col gap-2 border-t border-border pt-4 sm:flex-row">
                <Button size="sm" className="gap-1.5" onClick={handleSaveAndResolve} disabled={actionLoading}>
                  {actionLoading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Check className="h-3.5 w-3.5" />}
                  Save &amp; Resolve
                </Button>
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground"
                  onClick={handleDismiss} disabled={actionLoading}>
                  <X className="h-3.5 w-3.5" />
                  Image is fine
                </Button>
              </div>

            </div>
          )}
        </div>
      )}
      </>}

      {innerTab === "flagged" && (() => {
        if (flaggedLoading) {
          return (
            <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          );
        }
        if (flaggedError) {
          return (
            <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{flaggedError}</div>
          );
        }
        if (!flaggedEntries.length) {
          return (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="text-sm font-medium">No flagged URLs</p>
              <p className="text-xs">Nothing in the review queue.</p>
            </div>
          );
        }
        const totalPages = Math.max(1, Math.ceil(flaggedTotal / FLAGGED_PAGE_SIZE));
        return (
          <>
            {blockError && (
              <div className="mb-3 rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{blockError}</div>
            )}
            <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-border bg-muted/30">
                <h3 className="text-sm font-medium">Suspicious image URLs</h3>
                <p className="text-xs text-muted-foreground">
                  Every placeholder URL the scan currently knows about · {flaggedTotal} total.
                  Review-queue rows count events awaiting review and expand to them.
                  <strong> Detected</strong> rows are URLs no event is queued for — every event carrying one still
                  has a clean image. Their count is the events the last scan saw carrying it, which is a
                  lower bound: the scan reads pending events unless a full scan is enabled. Both can be blocked.
                </p>
              </div>
              <ul className="divide-y divide-border text-xs">
                {flaggedEntries.map(({ url, event_count, events, source, first_detected_at, allowed_by, allowed_at }) => {
                  const expanded = expandedUrl === url;
                  const listed = events || [];
                  const detected = source === "detected";
                  const allowedRow = source === "allowed";
                  // Only a review row's count is a queue depth; a detected or cleared row
                  // counts the events carrying the URL. Keyed off `review` rather than off
                  // `!detected`, which quietly called cleared rows "in review" once they
                  // became a third source.
                  const reviewRow = source === "review";
                  const countLabel = reviewRow
                    ? `${event_count} in review`
                    : `${event_count} ${event_count === 1 ? "event" : "events"}`;
                  // All three kinds expand. Detected and cleared rows get their events from an
                  // Elasticsearch aggregation rather than a table of their own, so the list can
                  // come back empty — a red index, or a URL no live event carries — and an
                  // expander that opens onto nothing is worse than no expander.
                  const expandable = listed.length > 0;
                  const undisclosed = event_count - listed.length;
                  return (
                    <li key={url} className="px-4 py-3 sm:px-5 sm:py-2.5">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                        <a href={url} target="_blank" rel="noreferrer" title={url}>
                          <UrlThumbnail url={url} />
                        </a>
                        <span className="truncate text-muted-foreground min-w-0" title={url}>{url}</span>
                        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
                          {detected && (
                            <Badge
                              variant="outline"
                              title={
                                first_detected_at
                                  ? `Detected by the scan since ${new Date(first_detected_at).toLocaleDateString()}; no event is queued for it because they all still have a clean image`
                                  : "Detected by the scan; no event is queued for it because they all still have a clean image"
                              }
                            >
                              Detected
                            </Badge>
                          )}
                          {allowedRow && (
                            <Badge
                              variant="success"
                              title={
                                allowed_at
                                  ? `Marked OK by ${allowed_by || "an admin"} on ${new Date(allowed_at).toLocaleDateString()}; the scan no longer flags it`
                                  : "Marked OK; the scan no longer flags it"
                              }
                            >
                              Marked OK
                            </Badge>
                          )}
                          {expandable ? (
                            <button
                              type="button"
                              onClick={() => setExpandedUrl(expanded ? null : url)}
                              aria-expanded={expanded}
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                              {countLabel}
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
                            </button>
                          ) : (
                            <span className="whitespace-nowrap px-2 py-1 text-muted-foreground">
                              {countLabel}
                            </span>
                          )}
                          {pendingUrl === url && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                          {allowedRow ? (
                            // The inverse action, on the row itself — a cleared URL has no other
                            // home, and one that vanished on click could never be un-clicked.
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={Boolean(pendingUrl)}
                              onClick={() => handleUnallowUrl(url)}
                              title="Put this URL back among the flagged ones. It is demoted again, not blocked."
                            >
                              Undo
                            </Button>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={Boolean(pendingUrl)}
                                onClick={() => handleAllowUrl(url)}
                                title="A real image the scan got wrong. Stops it being flagged and restores it on the site."
                              >
                                <Check className="mr-1 h-3.5 w-3.5" />
                                Not a placeholder
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={Boolean(pendingUrl)}
                                onClick={() => handleBlockUrl(url)}
                              >
                                Block
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      {expanded && expandable && (
                        <ul className="mt-3 space-y-2 border-t border-border pt-3">
                          {listed.map((event) => {
                            const webUrl = getEventWebUrl(event.id);
                            return (
                              <li key={event.id} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <EntityLink type="event" id={event.id} newTab className="truncate font-medium">
                                      {displayLabel(event.preferred_title, event.title) || event.id}
                                    </EntityLink>
                                    {(event.display_status || event.status) && (
                                      <EventStatusBadge status={event.display_status || event.status} />
                                    )}
                                  </div>
                                  <ProviderLabel preferred={event.preferred_title} original={event.title} />
                                  <p className="font-mono text-[11px] text-muted-foreground select-all">{event.id}</p>
                                </div>
                                {webUrl && (
                                  <a
                                    href={webUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex shrink-0 items-center gap-1 text-muted-foreground hover:text-foreground hover:underline"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    View in Applauss
                                  </a>
                                )}
                              </li>
                            );
                          })}
                          {undisclosed > 0 && (
                            <li className="text-muted-foreground">
                              …and {undisclosed} more{reviewRow ? " in review" : ""}, not listed.
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <Pagination page={flaggedPage} totalPages={totalPages} onPageChange={setFlaggedPage} />
          </>
        );
      })()}
    </div>
  );
}
